import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { _HttpClient } from '@delon/theme';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, delay, retry, timer } from 'rxjs';
import { of } from 'rxjs';
import { LicenseService } from '../../../../shared/services/license.service';

const MFA_PROMPT_DISMISSED_KEY = 'mfa_prompt_dismissed';
const MFA_PROMPT_DISMISSED_LATER_KEY = 'mfa_prompt_dismissed_later';
const MFA_PROMPT_DISMISSED_LATER_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

@Component({
  selector: 'app-mfa-prompt',
  templateUrl: './mfa-prompt.component.html',
  styleUrls: ['./mfa-prompt.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterModule, NzIconModule, NzButtonModule]
})
export class MfaPromptComponent implements OnInit, OnDestroy {
  showPrompt = signal<boolean>(false);
  loading = signal<boolean>(true);
  twoFactorEnabled = signal<boolean>(false);
  mfaEnforced = signal<boolean>(false);

  constructor(
    private http: _HttpClient,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private licenseService: LicenseService,
    private destroyRef: DestroyRef
  ) {}

  private hasChecked = false;

  /**
   * Check if the prompt was dismissed within the last 24 hours
   * @returns true if dismissed within 24 hours, false otherwise
   */
  private isDismissedWithin24Hours(): boolean {
    const dismissedLaterTimestamp = localStorage.getItem(MFA_PROMPT_DISMISSED_LATER_KEY);
    if (!dismissedLaterTimestamp) {
      return false;
    }

    try {
      const timestamp = parseInt(dismissedLaterTimestamp, 10);
      const now = Date.now();
      const timeSinceDismissal = now - timestamp;
      
      // If less than 24 hours have passed, consider it dismissed
      return timeSinceDismissal < MFA_PROMPT_DISMISSED_LATER_DURATION_MS;
    } catch {
      // If timestamp is invalid, clear it and return false
      localStorage.removeItem(MFA_PROMPT_DISMISSED_LATER_KEY);
      return false;
    }
  }

  ngOnInit(): void {
    // Check immediately, but also set up a delayed check in case license hasn't loaded yet
    // This ensures we check as soon as possible, but also catch cases where license loads later
    this.checkMfaStatus();

    // Also check again after a delay to catch license data that loads later
    timer(2000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Only re-check if we haven't completed the check yet (still loading or no result)
        // Also check if prompt hasn't been dismissed (permanently or within 24 hours)
        const dismissedPermanently = localStorage.getItem(MFA_PROMPT_DISMISSED_KEY) === 'true';
        const dismissedLater = this.isDismissedWithin24Hours();
        
        if (!dismissedPermanently && !dismissedLater && (this.loading() || (!this.showPrompt() && !this.hasChecked))) {
          this.checkMfaStatus();
        }
      });
  }

  checkMfaStatus(): void {
    // Check if user has dismissed the prompt permanently
    const dismissedPermanently = localStorage.getItem(MFA_PROMPT_DISMISSED_KEY);
    if (dismissedPermanently === 'true') {
      this.loading.set(false);
      this.showPrompt.set(false);
      this.hasChecked = true;
      this.cdr.detectChanges();
      return;
    }

    // Check if user has dismissed the prompt within the last 24 hours
    if (this.isDismissedWithin24Hours()) {
      this.loading.set(false);
      this.showPrompt.set(false);
      this.hasChecked = true;
      this.cdr.detectChanges();
      return;
    }

    // Check if company enforces MFA using LicenseService (reuse loaded information)
    // If license hasn't been checked yet, enforceTwoFactorAuthentication will be false (default)
    // In that case, we'll proceed to check user profile and show prompt if needed
    const isEnforced = this.licenseService.enforceTwoFactorAuthentication;
    this.mfaEnforced.set(isEnforced);

    // If MFA is enforced, don't show the prompt (the enforcement modal will handle it)
    if (isEnforced) {
      this.loading.set(false);
      this.showPrompt.set(false);
      this.hasChecked = true;
      this.cdr.detectChanges();
      return;
    }

    // If not enforced, check if user has MFA enabled
    this.http
      .get('api/user/profile')
      .pipe(
        catchError(() => {
          // If profile check fails, hide prompt
          this.loading.set(false);
          this.showPrompt.set(false);
          this.cdr.detectChanges();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: any) => {
          if (res !== null) {
            const hasMfaEnabled = res?.twoFactorEnabled === true;
            this.twoFactorEnabled.set(hasMfaEnabled);

            // Show prompt only if MFA is not enabled and not enforced
            this.showPrompt.set(!hasMfaEnabled);
            this.loading.set(false);
            this.hasChecked = true;
            this.cdr.detectChanges();
          } else {
            // If res is null (error case), ensure loading is set to false
            this.loading.set(false);
            this.hasChecked = true;
            this.cdr.detectChanges();
          }
        }
      });
  }

  onDismissLater(): void {
    // Hide the prompt for 24 hours by storing the current timestamp
    // The prompt will show again after 24 hours have passed
    const now = Date.now();
    localStorage.setItem(MFA_PROMPT_DISMISSED_LATER_KEY, now.toString());
    this.showPrompt.set(false);
    this.cdr.detectChanges();
  }

  onDismissPermanently(): void {
    // Save dismissal preference to local storage - user doesn't want to be reminded
    localStorage.setItem(MFA_PROMPT_DISMISSED_KEY, 'true');
    this.showPrompt.set(false);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // Component cleanup - takeUntilDestroyed() handles subscription cleanup automatically
  }

  navigateToSettings(): void {
    // Navigate to account security settings page with enable2FA query param
    // Don't dismiss - let user come back if they cancel
    this.router.navigate(['/account/settings/security'], {
      queryParams: { enable2FA: 'true' }
    });
    // Hide the prompt when navigating to settings
    this.showPrompt.set(false);
    this.cdr.detectChanges();
  }
}

